/// <reference path="../../ui/types.d.ts" />
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import {
    catchRejection,
    checkOrigin,
    checkResponse,
    getPostPromises,
    reloadLocation,
    showToast,
} from '../lib/crowdin-iframe-context';
import { useFieldDependencies } from '../lib/field-dependencies';

interface LoginField {
    key?: string;
    label?: string;
    labelHtml?: string;
    helpText?: string;
    helpTextHtml?: string;
    type?: string;
    defaultValue?: any;
    dependencySettings?: string;
    isMulti?: boolean;
    isSearchable?: boolean;
    options?: Array<{ value: string; label: string }>;
    noticeType?: string;
    noIcon?: boolean;
    accept?: string;
}

interface LoginPageProps {
    name: string;
    loginFields: LoginField[];
    oauthLogin?: boolean;
    oauthUrl?: string;
    oauthMode?: string;
}

interface LoginFieldRenderOptions {
    fileNames: Record<string, string>;
    fileValues: Record<string, string>;
    readFileData: (e: any, key: string) => void;
    registerField: (key: string) => (element: any) => void;
    registerFileInput: (key: string) => (element: HTMLInputElement | null) => void;
    uploadFiles: (key: string) => void;
}

const renderField = (
    field: LoginField,
    { fileNames, fileValues, readFileData, registerField, registerFileInput, uploadFiles }: LoginFieldRenderOptions,
) => {
    if (!field.key) {
        // Label-only field
        return field.labelHtml ? (
            <crowdin-p
                {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}
                dangerouslySetInnerHTML={{ __html: field.labelHtml }}
            />
        ) : (
            <crowdin-p {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}>
                {field.label}
            </crowdin-p>
        );
    }

    const commonProps = {
        id: field.key,
        label: field.label,
        ...(field.helpText ? { 'help-text': field.helpText } : {}),
        ...(field.helpTextHtml ? { 'help-text-html': field.helpTextHtml } : {}),
        ...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {}),
    };

    // Special field types
    if (['checkbox', 'select', 'textarea', 'file', 'notice'].includes(field.type || '')) {
        switch (field.type) {
            case 'checkbox':
                return (
                    <crowdin-checkbox
                        ref={registerField(field.key)}
                        {...commonProps}
                        value="false"
                        use-switch
                        {...(field.defaultValue === true ? { checked: String(field.defaultValue) } : {})}
                    />
                );

            case 'select':
                return (
                    <crowdin-select
                        ref={registerField(field.key)}
                        {...commonProps}
                        {...(field.isMulti ? { 'is-multi': true, 'close-on-select': 'false' } : {})}
                        {...(field.isSearchable ? { 'is-searchable': true } : {})}
                    >
                        {field.options?.map((option) => (
                            <option
                                key={option.value}
                                value={option.value}
                                {...(field.defaultValue === option.value ? { selected: true } : {})}
                            >
                                {option.label}
                            </option>
                        ))}
                    </crowdin-select>
                );

            case 'textarea':
                return (
                    <crowdin-textarea
                        ref={registerField(field.key)}
                        {...commonProps}
                        value={field.defaultValue || ''}
                    />
                );

            case 'notice':
                return (
                    <crowdin-alert
                        {...(field.noticeType ? { type: field.noticeType } : {})}
                        {...(field.label ? { title: field.label } : {})}
                        {...(field.noIcon ? { 'no-icon': String(field.noIcon) } : {})}
                        {...(field.dependencySettings ? { 'data-dependency': field.dependencySettings } : {})}
                        dangerouslySetInnerHTML={{ __html: field.helpTextHtml || field.helpText || '' }}
                    />
                );

            case 'file':
                return (
                    <div className="text-sm leading-6 font-normal text-[rgba(38,50,56,.87)] antialiased">
                        {field.helpText && <div className="text-xs text-[rgba(38,50,56,.54)]">{field.helpText}</div>}
                        {field.helpTextHtml && (
                            <div className="text-xs text-[rgba(38,50,56,.54)]">{field.helpTextHtml}</div>
                        )}
                        <div className="mt-2 flex items-center justify-between">
                            <crowdin-button outlined onClick={() => uploadFiles(field.key!)}>
                                {field.label}
                            </crowdin-button>
                            <textarea
                                ref={registerField(field.key)}
                                hidden
                                id={field.key}
                                value={fileValues[field.key] || ''}
                                readOnly
                            ></textarea>
                            <input
                                ref={registerFileInput(field.key)}
                                id={`store_${field.key}`}
                                data-id={field.key}
                                {...(field.accept ? { accept: field.accept } : {})}
                                onChange={(e) => readFileData(e, field.key!)}
                                hidden
                                type={field.type}
                            />
                            <div className="flex items-center italic">
                                <i>{fileNames[field.key] || 'No file'}</i>
                            </div>
                        </div>
                    </div>
                );
        }
    }

    // Default input field
    return (
        <crowdin-input
            ref={registerField(field.key)}
            {...commonProps}
            class="mb-2 block"
            {...(field.type ? { type: field.type } : {})}
            value={field.defaultValue || ''}
        />
    );
};

export const LoginPage: FC<LoginPageProps> = ({ name, loginFields, oauthLogin, oauthUrl, oauthMode }) => {
    const refreshIntervalId = useRef<any>(null);
    const fieldRefs = useRef<Record<string, any>>({});
    const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    const [fileNames, setFileNames] = useState<Record<string, string>>({});
    const [fileValues, setFileValues] = useState<Record<string, string>>({});
    const [isLoading, setIsLoading] = useState(false);
    useFieldDependencies(loginFields);

    // Cleanup interval on component unmount
    useEffect(() => {
        return () => {
            if (refreshIntervalId.current) {
                clearInterval(refreshIntervalId.current);
                refreshIntervalId.current = null;
            }
        };
    }, []);

    function oauthLoginHandler() {
        const url = oauthUrl;
        if (url) {
            openOAuthPopup(url);
            return;
        }

        setIsLoading(true);

        checkOrigin()
            .then((queryParams) =>
                fetch(`api/oauth-url${queryParams}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ loginForm: getLoginForm() }),
                }),
            )
            .then(checkResponse)
            .then((res) => openOAuthPopup(res.url))
            .catch((e) => {
                setIsLoading(false);
                catchRejection(e, "Can't get oauth token");
            });
    }

    function openOAuthPopup(url: string) {
        window.open(url, name, 'location=0,status=0,width=800,height=400');

        if (oauthMode === 'polling') {
            if (refreshIntervalId.current) {
                clearInterval(refreshIntervalId.current);
            }

            refreshIntervalId.current = setInterval(
                () =>
                    checkOrigin()
                        .then((queryParams) => fetch(`api/oauth-polling${queryParams}`, { method: 'POST' }))
                        .then(checkResponse)
                        .then((res) => {
                            if (res.oauthCreds) {
                                integrationLogin({
                                    refreshToken: res.oauthCreds.refreshToken,
                                    accessToken: res.oauthCreds.accessToken,
                                    expireIn: res.oauthCreds.expireIn,
                                    timestamp: res.oauthCreds.timestamp,
                                });
                                if (refreshIntervalId.current) {
                                    clearInterval(refreshIntervalId.current);
                                }
                            }
                        }),
                2000,
            );
            return;
        }

        getPostPromises()['oauth_popup'] = {
            resolve: (data: any) => {
                if (data.error) {
                    showToast(data.error);
                    return;
                }
                integrationLogin({
                    refreshToken: data.refreshToken,
                    accessToken: data.accessToken,
                    expireIn: data.expireIn,
                    timestamp: data.timestamp,
                });
            },
        };
    }

    function integrationLogin(oauthCredentials?: any) {
        const credentials = oauthCredentials || getLoginForm();

        setIsLoading(true);

        checkOrigin()
            .then((queryParams) =>
                fetch(`api/login${queryParams}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ credentials }),
                }),
            )
            .then(checkResponse)
            .then(() => localStorage.removeItem(`revised_${name}`))
            .then(reloadLocation)
            .catch((e) => {
                const errorMessage = e.error?.message || e.message || 'Credentials are not stored';

                setIsLoading(false);

                catchRejection(e, errorMessage);
            });
    }

    function getLoginForm() {
        return loginFields
            .filter((f) => f.key)
            .reduce((acc, f) => {
                const key = f.key!;
                const field = fieldRefs.current[key];

                if (f.type === 'checkbox') {
                    acc[key] = !!field?.checked;
                } else if (f.type === 'file') {
                    acc[key] = fileValues[key] || field?.value || '';
                } else {
                    acc[key] = field?.getAttribute?.('value') || field?.value;
                }
                return acc;
            }, {} as any);
    }

    function uploadFiles(id: string) {
        const input = fileInputRefs.current[id];

        if (input) {
            input.value = '';
            input.click();
        }
    }

    function readFileData(event: any, identifier: string) {
        const reader = new FileReader();
        const file = event.target.files[0];

        if (!file) {
            return;
        }

        setFileNames((currentFileNames) => ({ ...currentFileNames, [identifier]: file.name }));

        reader.onloadstart = () => setIsLoading(true);

        reader.onloadend = () => setIsLoading(false);

        reader.onload = () => {
            setFileValues((currentFileValues) => ({
                ...currentFileValues,
                [identifier]: typeof reader.result === 'string' ? reader.result : '',
            }));
        };

        reader.readAsText(file);
    }

    function registerField(key?: string) {
        return (element: any) => {
            if (!key) {
                return;
            }

            if (element) {
                fieldRefs.current[key] = element;
            } else {
                delete fieldRefs.current[key];
            }
        };
    }

    function registerFileInput(key?: string) {
        return (element: HTMLInputElement | null) => {
            if (!key) {
                return;
            }

            fileInputRefs.current[key] = element;
        };
    }

    return (
        <div>
            <div className="mx-auto flex min-h-[calc(100vh-64px)] max-w-[1420px] items-center justify-around px-6 py-4 text-center">
                <div>
                    <crowdin-card is-shadowed is-padding-lg class="mb-2.5 overflow-visible">
                        <img
                            alt={`${name} logo`}
                            src="logo.png"
                            className="mx-auto h-auto max-h-[70px] w-auto max-w-[70px]"
                        />
                        <crowdin-h4 id="integration-name" class="my-2 mb-4">
                            {name}
                        </crowdin-h4>
                        <div className="mb-6 text-left">
                            {loginFields.map((field, fieldIndex) => (
                                <Fragment key={field.key || field.label || field.labelHtml || fieldIndex}>
                                    {renderField(field, {
                                        fileNames,
                                        fileValues,
                                        readFileData,
                                        registerField,
                                        registerFileInput,
                                        uploadFiles,
                                    })}

                                    <div
                                        style={{ padding: '8px' }}
                                        {...(field.dependencySettings
                                            ? { 'data-dependency': field.dependencySettings }
                                            : {})}
                                    ></div>
                                </Fragment>
                            ))}
                        </div>
                        <crowdin-button
                            id="login-button"
                            disabled={isLoading}
                            is-loading={isLoading}
                            outlined
                            icon-after="arrow_forward"
                            onClick={oauthLogin ? () => oauthLoginHandler() : () => integrationLogin()}
                        >
                            Log In With {name}
                        </crowdin-button>
                    </crowdin-card>
                </div>
            </div>
            <crowdin-toasts></crowdin-toasts>
        </div>
    );
};
